import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const backdrop = {
  visible: { opacity: 1, transition: { duration: 0.3 } },
  hidden: { opacity: 0, transition: { duration: 0.3 } },
};

const modal = {
  hidden: { opacity: 0, scale: 0.7, y: 50 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20,
      duration: 0.4,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.7,
    y: 50,
    transition: { duration: 0.3 },
  },
};

const ConsultationModal = ({
  showModal,
  setShowModal,
  ctaType = 'general',
  specificService = '',
  companyName = '',
}) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: '',
    phone: '',
    specific_service: specificService,
    company_name: companyName,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    message: '',
    phone: '',
    specific_service: '',
    company_name: '',
  });

  const modalConfig = {
    general: {
      title: 'Connect with Navam',
      subtext: 'Let’s Discuss Your Needs and Opportunities',
      messagePlaceholder: 'Tell us how we can assist you',
      buttonText: 'Send Request',
    },
    career: {
      title: 'Ignite Your Career Transformation with Navam',
      subtext: 'Share Your Career Goals and Let Us Guide Your Success',
      messagePlaceholder: 'Tell us about your career goals or aspirations',
      buttonText: 'Start Your Journey',
    },
    hrService: {
      title: 'Elevate Your HR Solutions with Navam',
      subtext: 'Unlock Tailored HR Strategies for Your Organization',
      messagePlaceholder: 'Describe your HR challenges or requirements',
      buttonText: 'Request HR Solutions',
    },
    specificService: {
      title: `Explore ${specificService || 'Custom Solutions'} with Navam`,
      subtext: `Partner with Us to Achieve Your Goals${companyName ? ` at ${companyName}` : ''}`,
      messagePlaceholder: 'Share details about your specific needs',
      buttonText: 'Submit Inquiry',
    },
  };

  const config = modalConfig[ctaType] || modalConfig.general;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    if (name === 'message') {
      const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
      setFormErrors({
        ...formErrors,
        message: wordCount < 2 && value.trim() ? 'Please enter at least 2 words' : '',
      });
    } else if (name === 'name') {
      setFormErrors({
        ...formErrors,
        name: value.trim() ? '' : 'Name is required',
      });
    } else if (name === 'email') {
      setFormErrors({
        ...formErrors,
        email: value.trim() ? (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Invalid email format') : 'Email is required',
      });
    } else if (name === 'phone') {
      setFormErrors({
        ...formErrors,
        phone: value.trim() && !/^\+?[\d\s-]{7,}$/.test(value) ? 'Invalid phone number' : '',
      });
    } else if (name === 'specific_service') {
      setFormErrors({
        ...formErrors,
        specific_service: value.trim() ? '' : 'Specific service is required',
      });
    } else if (name === 'company_name') {
      setFormErrors({
        ...formErrors,
        company_name: value.trim() ? '' : 'Company name is required',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (!form.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Invalid email format';
    if (form.phone.trim() && !/^\+?[\d\s-]{7,}$/.test(form.phone)) errors.phone = 'Invalid phone number';
    const wordCount = form.message.trim().split(/\s+/).filter(Boolean).length;
    if (!form.message.trim()) errors.message = 'Message is required';
    else if (wordCount < 2) errors.message = 'Please enter at least 2 words';
    if (ctaType === 'specificService') {
      if (!form.specific_service.trim()) errors.specific_service = 'Specific service is required';
      if (!form.company_name.trim()) errors.company_name = 'Company name is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    setError(null);
    setFormErrors({ name: '', email: '', message: '', phone: '', specific_service: '', company_name: '' });

    const payload = {
      name: form.name,
      email: form.email,
      message: form.message,
      type: ctaType,
      phone: form.phone || null,
    };

    if (ctaType === 'specificService') {
      payload.specific_service = form.specific_service;
      payload.company_name = form.company_name;
    }

    const backendUrl = 'https://navam-web-server.onrender.com/api/send_email';
    const apiKey = process.env.REACT_APP_API_KEY;

    try {
      const response = await fetch(backendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': apiKey,
        },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(60000), // Timeout for Render spin-down
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit form');
      }

      setSubmitted(true);
      setForm({
        name: '',
        email: '',
        message: '',
        phone: '',
        specific_service: '',
        company_name: ''
      });
    } catch (err) {
      setError(err.name === 'TimeoutError' ? 'Request timed out. Please try again.' : err.message || 'Something went wrong.');
      console.error('Submission error:', err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <AnimatePresence>
      {showModal && (
        <motion.div
          className="fixed inset-0 bg-gradient-to-b from-black/70 to-black/50 z-50 flex items-center justify-center px-4"
          variants={backdrop}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={() => setShowModal(false)}
        >
          <motion.div
            className="bg-white w-full max-w-2xl rounded-2xl p-8 shadow-2xl relative overflow-hidden"
            variants={modal}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#0e6d87]/10 to-blue-100/10" />
            <button
              className="absolute top-4 right-4 text-gray-600 text-2xl font-bold hover:text-red-500 transition-transform hover:scale-110"
              onClick={() => setShowModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="text-center mb-6 relative z-10">
              <motion.h2
                className="text-2xl md:text-4xl font-extrabold bg-gradient-to-r from-[#0e6d87] to-blue-600 bg-clip-text text-transparent tracking-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {config.title}
              </motion.h2>
              <motion.h4
                className="text-base md:text-lg text-gray-600 font-medium mt-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {config.subtext}
              </motion.h4>
            </div>
            {submitted ? (
              <motion.div
                className="text-center text-green-600 font-medium text-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div className="flex items-center justify-center mb-4">
                  <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                Email sent successfully! Our team will connect with you soon.
              </motion.div>
            ) : (
              <form className="space-y-5 relative z-10" onSubmit={handleSubmit}>
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base focus:ring-2 focus:ring-[#0e6d87] focus:border-transparent transition-all bg-white/80"
                  />
                  {formErrors.name && (
                    <motion.p
                      className="text-red-600 text-sm mt-1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {formErrors.name}
                    </motion.p>
                  )}
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base focus:ring-2 focus:ring-[#0e6d87] focus:border-transparent transition-all bg-white/80"
                  />
                  {formErrors.email && (
                    <motion.p
                      className="text-red-600 text-sm mt-1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {formErrors.email}
                    </motion.p>
                  )}
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Your Phone (Optional)"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base focus:ring-2 focus:ring-[#0e6d87] focus:border-transparent transition-all bg-white/80"
                  />
                  {formErrors.phone && (
                    <motion.p
                      className="text-red-600 text-sm mt-1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {formErrors.phone}
                    </motion.p>
                  )}
                </div>
                {ctaType === 'specificService' && (
                  <>
                    <div>
                      <input
                        type="text"
                        name="specific_service"
                        placeholder="Specific Service"
                        value={form.specific_service}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base focus:ring-2 focus:ring-[#0e6d87] focus:border-transparent transition-all bg-white/80"
                      />
                      {formErrors.specific_service && (
                        <motion.p
                          className="text-red-600 text-sm mt-1"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          {formErrors.specific_service}
                        </motion.p>
                      )}
                    </div>
                    <div>
                      <input
                        type="text"
                        name="company_name"
                        placeholder="Company Name"
                        value={form.company_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base focus:ring-2 focus:ring-[#0e6d87] focus:border-transparent transition-all bg-white/80"
                      />
                      {formErrors.company_name && (
                        <motion.p
                          className="text-red-600 text-sm mt-1"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          {formErrors.company_name}
                        </motion.p>
                      )}
                    </div>
                  </>
                )}
                <div>
                  <textarea
                    name="message"
                    placeholder={config.messagePlaceholder}
                    value={form.message}
                    onChange={handleChange}
                    rows="5"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-base focus:ring-2 focus:ring-[#0e6d87] focus:border-transparent transition-all bg-white/80"
                  />
                  {formErrors.message && (
                    <motion.p
                      className="text-red-600 text-sm mt-1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {formErrors.message}
                    </motion.p>
                  )}
                </div>
                {error && (
                  <motion.div
                    className="text-red-600 text-center text-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {error}
                  </motion.div>
                )}
                <motion.button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#0e6d87] to-blue-600 text-white px-6 py-3 rounded-lg font-semibold text-base hover:shadow-lg transition-all disabled:opacity-70"
                  disabled={loading || Object.values(formErrors).some((err) => err)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    config.buttonText
                  )}
                </motion.button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConsultationModal;